// Time zones for stays. A stay has no airport, so its zone comes from a flight landing in
// its city, else from a table of cities and single-zone countries, else it stays unknown
// (shown as local times with no reminder). Pure, shared by the app, the server and tests.

import type { Place, Segment } from './types.ts';
import { AIRPORTS, airportInfo } from './airports.ts';

/** Towns we stay in that the airport table doesn't name (and other names for its cities). */
const CITY_ZONES: Record<string, string> = {
  brooklyn: 'America/New_York',
  manhattan: 'America/New_York',
  queens: 'America/New_York',
  'new york city': 'America/New_York',
  'jersey city': 'America/New_York',
  hoboken: 'America/New_York',
  'the hamptons': 'America/New_York',
  montauk: 'America/New_York',
  'cape cod': 'America/New_York',
  'lake placid': 'America/New_York',
  'santa monica': 'America/Los_Angeles',
  'palm springs': 'America/Los_Angeles',
  napa: 'America/Los_Angeles',
  sonoma: 'America/Los_Angeles',
  'big sur': 'America/Los_Angeles',
  tahoe: 'America/Los_Angeles',
  'lake tahoe': 'America/Los_Angeles',
  'park city': 'America/Denver',
  aspen: 'America/Denver',
  vail: 'America/Denver',
  sedona: 'America/Phoenix',
  sintra: 'Europe/Lisbon',
  cascais: 'Europe/Lisbon',
  lisboa: 'Europe/Lisbon',
  roma: 'Europe/Rome',
  milano: 'Europe/Rome',
  venezia: 'Europe/Rome',
  napoli: 'Europe/Rome',
  wien: 'Europe/Vienna',
  munchen: 'Europe/Berlin',
  praha: 'Europe/Prague',
  kobenhavn: 'Europe/Copenhagen',
  florence: 'Europe/Rome',
  firenze: 'Europe/Rome',
  positano: 'Europe/Rome',
  amalfi: 'Europe/Rome',
  sorrento: 'Europe/Rome',
  'cinque terre': 'Europe/Rome',
  como: 'Europe/Rome',
  siena: 'Europe/Rome',
  santorini: 'Europe/Athens',
  mykonos: 'Europe/Athens',
  kyoto: 'Asia/Tokyo',
  nara: 'Asia/Tokyo',
  hakone: 'Asia/Tokyo',
  nikko: 'Asia/Tokyo',
  kanazawa: 'Asia/Tokyo',
  'hoi an': 'Asia/Ho_Chi_Minh',
  'da nang': 'Asia/Ho_Chi_Minh',
  saigon: 'Asia/Ho_Chi_Minh',
  ubud: 'Asia/Makassar',
  seminyak: 'Asia/Makassar',
  canggu: 'Asia/Makassar',
  'chiang rai': 'Asia/Bangkok',
  'koh samui': 'Asia/Bangkok',
  'ko samui': 'Asia/Bangkok',
  'ko phi phi': 'Asia/Bangkok',
};

/** Countries with one zone (mainland, where the islands differ): the address's last part. */
const COUNTRY_ZONES: Record<string, string> = {
  portugal: 'Europe/Lisbon',
  spain: 'Europe/Madrid',
  espana: 'Europe/Madrid',
  france: 'Europe/Paris',
  italy: 'Europe/Rome',
  italia: 'Europe/Rome',
  germany: 'Europe/Berlin',
  deutschland: 'Europe/Berlin',
  switzerland: 'Europe/Zurich',
  austria: 'Europe/Vienna',
  netherlands: 'Europe/Amsterdam',
  belgium: 'Europe/Brussels',
  'united kingdom': 'Europe/London',
  uk: 'Europe/London',
  england: 'Europe/London',
  scotland: 'Europe/London',
  ireland: 'Europe/Dublin',
  iceland: 'Atlantic/Reykjavik',
  greece: 'Europe/Athens',
  croatia: 'Europe/Zagreb',
  'czech republic': 'Europe/Prague',
  czechia: 'Europe/Prague',
  hungary: 'Europe/Budapest',
  poland: 'Europe/Warsaw',
  denmark: 'Europe/Copenhagen',
  sweden: 'Europe/Stockholm',
  norway: 'Europe/Oslo',
  finland: 'Europe/Helsinki',
  bulgaria: 'Europe/Sofia',
  turkey: 'Europe/Istanbul',
  turkiye: 'Europe/Istanbul',
  morocco: 'Africa/Casablanca',
  japan: 'Asia/Tokyo',
  'south korea': 'Asia/Seoul',
  korea: 'Asia/Seoul',
  'republic of korea': 'Asia/Seoul',
  taiwan: 'Asia/Taipei',
  thailand: 'Asia/Bangkok',
  vietnam: 'Asia/Ho_Chi_Minh',
  'viet nam': 'Asia/Ho_Chi_Minh',
  singapore: 'Asia/Singapore',
  philippines: 'Asia/Manila',
  china: 'Asia/Shanghai',
  'hong kong': 'Asia/Hong_Kong',
  india: 'Asia/Kolkata',
  'united arab emirates': 'Asia/Dubai',
  uae: 'Asia/Dubai',
  'new zealand': 'Pacific/Auckland',
  'costa rica': 'America/Costa_Rica',
  'puerto rico': 'America/Puerto_Rico',
  colombia: 'America/Bogota',
  peru: 'America/Lima',
};

/** "Málaga, Spain" → "malaga, spain": lower case, no accents, single spaces. */
function norm(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9,]+/g, ' ')
    .replace(/\s*,\s*/g, ',')
    .trim();
}

let cities: Map<string, string> | undefined;
/** Every city we know a zone for, by normalized name. */
function cityTable(): Map<string, string> {
  if (!cities) {
    cities = new Map();
    const ambiguous = new Set<string>();
    for (const [c, tz] of [...Object.values(AIRPORTS).map((a) => [a.city, a.timeZone]), ...Object.entries(CITY_ZONES)]) {
      const key = norm(c);
      if (cities.has(key) && cities.get(key) !== tz) ambiguous.add(key);
      cities.set(key, tz);
    }
    // Two places, two zones (Portland, Maine and Oregon): the name alone can't say which.
    for (const key of ambiguous) cities.delete(key);
  }
  return cities;
}

/** Whether `name` appears in `text` as whole words ("Lisbon" in "Rua X, 1100 Lisbon"). */
function mentions(text: string, name: string): boolean {
  return !!name && ` ${text.replace(/,/g, ' ')} `.includes(` ${name} `);
}

/** The place's town and address, normalized, for matching. */
function placeText(p: Place | undefined): string {
  return norm([p?.city, p?.address].filter(Boolean).join(', '));
}

/** The zone of a place from its city, a city named in its address, or its country. */
export function placeZone(p: Place | undefined): string | undefined {
  const table = cityTable();
  if (p?.city) {
    const tz = table.get(norm(p.city));
    if (tz) return tz;
  }
  const text = placeText(p);
  if (!text) return undefined;
  // Longest name first, so "New York City" wins over "York".
  const named = [...table.keys()].filter((c) => mentions(text, c)).sort((a, b) => b.length - a.length)[0];
  if (named) return table.get(named);
  const country = text.split(',').at(-1)?.trim();
  return country ? COUNTRY_ZONES[country] : undefined;
}

/** A flight's arrival city, from the segment or its airport. */
function arrivalCity(f: Segment): string | undefined {
  return f.to?.city ?? airportInfo(f.to?.code)?.city;
}

/**
 * The zone for a stay: a flight landing in its city (the latest one landing by check-in,
 * else any), else the city/country table, else undefined.
 */
export function stayZone(stay: Pick<Segment, 'start' | 'place'>, segments: readonly Segment[]): string | undefined {
  const text = placeText(stay.place);
  if (text) {
    const landings = segments
      .filter((f) => f.type === 'flight' && f.status !== 'cancelled' && f.to?.timeZone)
      .filter((f) => {
        const city = arrivalCity(f);
        return !!city && mentions(text, norm(city));
      })
      .sort((a, b) => (a.end?.date ?? a.start.date).localeCompare(b.end?.date ?? b.start.date));
    const byCheckIn = landings.filter((f) => (f.end?.date ?? f.start.date) <= stay.start.date).at(-1);
    const flight = byCheckIn ?? landings[0];
    if (flight) return flight.to!.timeZone;
  }
  return placeZone(stay.place);
}

/**
 * The stay with its zone worked out again and set on the place, check-in and check-out
 * (cleared when unknown, so a stale zone doesn't outlive a changed address). Other kinds
 * of booking are returned as they are.
 */
export function withStayZone<T extends Pick<Segment, 'type' | 'start' | 'end' | 'place'>>(stay: T, segments: readonly Segment[]): T {
  if (stay.type !== 'stay') return stay;
  const timeZone = stayZone(stay, segments);
  const zoned = <L extends { timeZone?: string }>(x: L): L => {
    const { timeZone: _, ...rest } = x;
    return (timeZone ? { ...rest, timeZone } : rest) as L;
  };
  return {
    ...stay,
    place: stay.place && zoned(stay.place),
    start: zoned(stay.start),
    end: stay.end && zoned(stay.end),
  };
}
